import { FC } from 'react'

import { useSearchParams } from '@remix-run/react'

import { Container, PageHeading, SectionHeading } from '~/components'
import {
  LinkToMap,
  OpenSearchForm,
  SearchResultCount,
  SelectSearchResultOrder,
  useSelectSearchResultOrder,
  OfficeDetailCard,
  SearchResultOrder,
} from '~/features/common'
import { useModal } from '~/hooks/use-modal'
import { useWatchElementVisibility } from '~/hooks/use-watch-element-visibility'
import { twMerge } from '~/lib/utils'
import { Area, Facility, Type } from '~/models/types'

import { SearchFormArea, NoResultNotice, SearchCondition } from '../components'
import { Pagination } from '../components/pagination'

export { searchFormSchema } from '../components'

export type SearchResultPageProps = {
  prefectureName: string
  searchResultCount: number
  currentPage: number
  changePage: (page: number) => void
  isFromTopPage: boolean
  searchConditionText: string
  sortOrder: SearchResultOrder
  changeSortOrder: (order: SearchResultOrder) => void
  offices: {
    id: number
    facilityName: string
    address: string
    buildingName: string
    transportationInfo: string
    otherAccess: string
    officeDetailLink: string
    contactLink: string
    imageSrc: string
    spaces: {
      id: number
      types: string[]
      price: number | null
      priceTaxIncluded: number | null
      pricePrivateFlag: boolean
      capacity: number | null
    }[]
  }[]
  areas: Pick<Area, 'id' | 'name'>[]
  officeTypes: Type[]
  facilities: Pick<Facility, 'id' | 'name' | 'order'>[]
  initialFormValues: {
    areas?: string[]
    officeTypes?: string[]
    facilities?: string[]
    minPeople?: string
    minMonthlyRent?: string
    maxMonthlyRent?: string
    keywords?: string
  }
  prefectureSlug: string
}

const useSpFixedMapLink = () => {
  const mapButtonVisibility = useWatchElementVisibility()
  const mapSectionVisibility = useWatchElementVisibility()

  // nullが初期値のため、isVisibleがfalseかどうかで判定する
  const showsSpFixedMapLink =
    mapButtonVisibility.isVisible === false &&
    mapSectionVisibility.isVisible === false

  return {
    showsSpFixedMapLink,
    upperLinkToMapRef: mapButtonVisibility.elementRef,
    lowerMapSectionRef: mapSectionVisibility.elementRef,
  }
}

// OPTIMIZE: 条件変更ボタンが上部に固定される影響で、下部固定の地図を表示ボタンの表示/非表示が切り替わるタイミングが若干ずれるかも
//  useWatchElementVisibility を使用するときにIntersectionObserverの rootMargin を設定できるようになるといいかも
const useSpFixedSearchButton = () => {
  const upperSearchConditionVisibility = useWatchElementVisibility()
  const lowerSearchConditionVisibility = useWatchElementVisibility()

  // nullが初期値のため、isVisibleがfalseかどうかで判定する
  const showsSpFixedSearchButton =
    upperSearchConditionVisibility.isVisible === false &&
    lowerSearchConditionVisibility.isVisible === false

  return {
    showsSpFixedSearchButton,
    upperSearchConditionRef: upperSearchConditionVisibility.elementRef,
    lowerSearchConditionRef: lowerSearchConditionVisibility.elementRef,
  }
}

const SEARCH_RESULT_PER_PAGE = 30

const usePagination = (
  searchResultCount: number,
  currentPage: number,
  changePage: (page: number) => void,
) => {
  const totalPageCount = Math.ceil(searchResultCount / SEARCH_RESULT_PER_PAGE)

  const handleNext = () => {
    if (currentPage >= totalPageCount) {
      return
    }

    changePage(currentPage + 1)
  }

  const handlePrevious = () => {
    if (currentPage === 1) {
      return
    }

    changePage(currentPage - 1)
  }

  const handlePageChange = (page: number) => {
    if (page === currentPage) {
      return
    }

    changePage(page)
  }

  return {
    totalPageCount,
    handlePageChange,
    handleNext,
    handlePrevious,
  }
}

const useMapLink = (prefectureSlug: string) => {
  const [searchParams] = useSearchParams()

  // NOTE: 地図検索ではエリアとページの指定はできないため削除する
  searchParams.delete('page')

  // NOTE: 2025/04/03 zui エリアの検索結果を地図上に表示するため、エリアの指定を削除しない
  // searchParams.delete('areas')

  // NOTE: 表示する地図の中心座標を都道府県を用いて指定
  searchParams.set('prefectureSlug', prefectureSlug)

  const mapLink = `/map?${searchParams.toString()}`

  return {
    mapLink,
  }
}

export const SearchResultPage: FC<SearchResultPageProps> = ({
  prefectureName,
  searchResultCount,
  offices,
  changePage,
  currentPage,
  isFromTopPage,
  sortOrder,
  changeSortOrder,
  searchConditionText,
  areas,
  officeTypes,
  facilities,
  initialFormValues,
  prefectureSlug,
}) => {
  const { open, setOpen, onSelect, placeholder, searchResultOrders } =
    useSelectSearchResultOrder(sortOrder, changeSortOrder)

  const { showsSpFixedMapLink, upperLinkToMapRef, lowerMapSectionRef } =
    useSpFixedMapLink()

  const { mapLink } = useMapLink(prefectureSlug)

  const {
    showsSpFixedSearchButton,
    upperSearchConditionRef,
    lowerSearchConditionRef,
  } = useSpFixedSearchButton()

  const { handleNext, handlePageChange, handlePrevious, totalPageCount } =
    usePagination(searchResultCount, currentPage, changePage)

  const { isOpen, animation, openModal, closeModal } = useModal()

  const existsSearchResult = searchResultCount > 0
  const noSearchResults = searchResultCount === 0

  const areaFormOptions = areas.map(({ id, name }) => ({
    label: name,
    value: id.toString(),
  }))

  const officeTypeFormOptions = officeTypes.map(({ id, name }) => ({
    label: name,
    value: id.toString(),
  }))

  const facilitiesAndServicesFormOptions = facilities
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))
    .map(({ id, name }) => ({
      label: name,
      facilityId: id.toString(),
    }))

  return (
    <main className="pb-10 md:pb-16 md:pt-10">
      <div
        className={twMerge(
          'flex justify-center md:hidden',
          'fixed bottom-4 left-0 right-0 transition-transform duration-300',
          showsSpFixedMapLink
            ? 'translate-y-0'
            : 'translate-y-[calc(100%+16px)]',
        )}
      >
        <LinkToMap
          className="h-16"
          href={mapLink}
          iconIdSuffix="sp-fixed-button"
          variant="over"
        />
      </div>
      <div
        className={twMerge(
          'flex justify-center bg-white px-4 py-2.5 md:hidden',
          'fixed left-0 right-0 top-[calc(-52px-10px-10px)] transition-transform duration-300',
          showsSpFixedSearchButton
            ? 'translate-y-[calc(100%)]'
            : 'translate-y-0',
        )}
      >
        <OpenSearchForm onClick={openModal} size="medium">
          条件を変更する
        </OpenSearchForm>
      </div>
      <div className="bg-grayBg md:hidden">
        <Container>
          <SearchCondition
            isNarrow
            onClick={openModal}
            ref={upperSearchConditionRef}
            searchConditionText={searchConditionText}
          />
        </Container>
      </div>
      <Container className="mt-6 md:mt-0">
        <div className="md:flex md:flex-row md:gap-x-8 lg:gap-x-16">
          <div className="md:w-[300px]">
            <SearchFormArea
              animation={animation}
              areaFormOptions={areaFormOptions}
              closeModal={closeModal}
              facilitiesAndServicesFormOptions={
                facilitiesAndServicesFormOptions
              }
              initialValues={initialFormValues}
              isOpen={isOpen}
              mapLink={mapLink}
              officeTypeFormOptions={officeTypeFormOptions}
            />
          </div>
          <div className="w-full md:max-w-[calc(700px-300px-32px)] lg:max-w-[calc(896px-300px-64px)] xl:max-w-[calc(1120px-300px-64px)] 2xl:max-w-[844px]">
            <div>
              {!isFromTopPage && (
                <PageHeading>
                  {prefectureName}のシェアオフィス・コワーキングスペース一覧
                </PageHeading>
              )}
              {isFromTopPage && <PageHeading>新着オフィス</PageHeading>}
              <div className="lg:flex lg:flex-row lg:justify-between">
                <div className="mt-4 flex flex-row justify-between lg:justify-normal lg:gap-4">
                  <SearchResultCount
                    count={searchResultCount}
                    preCountText="検索結果"
                  />
                  <SelectSearchResultOrder
                    items={searchResultOrders}
                    onSelect={onSelect}
                    open={open}
                    placeholder={placeholder}
                    setOpen={setOpen}
                    size="medium"
                  />
                </div>
                {existsSearchResult && (
                  <div className="mt-4" ref={upperLinkToMapRef}>
                    <LinkToMap
                      className="w-full lg:w-[226px]"
                      href={mapLink}
                      iconIdSuffix="upper-button"
                      variant="mini"
                    />
                  </div>
                )}
              </div>
            </div>
            {noSearchResults && (
              <div className="mt-6 md:mt-16">
                <NoResultNotice onClick={openModal} />
              </div>
            )}
            {existsSearchResult && (
              <div className="mt-6 hidden md:block">
                <div className="flex flex-col gap-6">
                  {offices.map((office) => (
                    <OfficeDetailCard
                      address={office.address}
                      buildingName={office.buildingName}
                      contactLink={office.contactLink}
                      facilityName={office.facilityName}
                      imageSrc={office.imageSrc}
                      key={office.id}
                      officeDetailLink={office.officeDetailLink}
                      otherAccessInfo={office.otherAccess}
                      spaces={office.spaces}
                      transportationInfo={office.transportationInfo}
                    />
                  ))}
                </div>
              </div>
            )}
            {!!totalPageCount && (
              <div className="mt-6 hidden md:block">
                <Pagination
                  currentPage={currentPage}
                  handleNext={handleNext}
                  handlePageClick={handlePageChange}
                  handlePrevious={handlePrevious}
                  totalPageCount={totalPageCount}
                />
              </div>
            )}
          </div>
        </div>
      </Container>
      {existsSearchResult && (
        <div className="mt-6 md:hidden">
          <div className="flex flex-col gap-6">
            {offices.map((office) => (
              <OfficeDetailCard
                address={office.address}
                buildingName={office.buildingName}
                contactLink={office.contactLink}
                facilityName={office.facilityName}
                imageSrc={office.imageSrc}
                key={office.id}
                officeDetailLink={office.officeDetailLink}
                otherAccessInfo={office.otherAccess}
                spaces={office.spaces}
                transportationInfo={office.transportationInfo}
              />
            ))}
          </div>
        </div>
      )}
      <Container>
        {!!totalPageCount && (
          <div className="mt-6 md:hidden">
            <Pagination
              currentPage={currentPage}
              handleNext={handleNext}
              handlePageClick={handlePageChange}
              handlePrevious={handlePrevious}
              totalPageCount={totalPageCount}
            />
          </div>
        )}
        {existsSearchResult && (
          <div className="mt-16 md:hidden">
            <SearchCondition
              onClick={openModal}
              ref={lowerSearchConditionRef}
              searchConditionText={searchConditionText}
            />
          </div>
        )}
        <section className="mt-10 md:hidden" ref={lowerMapSectionRef}>
          <SectionHeading color="black">地図で探す</SectionHeading>
          <div
            className="mt-4"
            style={{ backgroundImage: `url('/images/map2.png')` }}
          >
            <LinkToMap
              className="h-[188px] md:h-[132px]"
              href={mapLink}
              iconIdSuffix="sp-map-section"
              variant="button"
            />
          </div>
        </section>
      </Container>
    </main>
  )
}
